<template>
	<SlidingSheet
		:is-open="isOpen"
		:title="`SCIM Logs - ${configurationName}`"
		:width="sheetWidth"
		@close="$emit('close')"
	>
		<template #content>
			<!-- Loading skeleton -->
			<div
				v-if="loading"
				class="logs-list"
			>
				<div
					v-for="n in skeletonCount"
					:key="`skeleton-${n}`"
					class="log-skeleton"
				>
					<div class="skeleton-header">
						<div class="skeleton-date" />
						<div class="skeleton-status" />
					</div>
					<div class="skeleton-message" />
				</div>
			</div>

			<!-- Error state -->
			<div
				v-else-if="loadError"
				class="error-state"
			>
				<v-icon
					size="48"
					color="error"
					class="mb-4"
				>
					mdi-alert-circle-outline
				</v-icon>
				<p class="error-message">
					Failed to load SCIM logs. Please try again.
				</p>
				<SecondaryActionButton @click="loadLogs">
					Retry
				</SecondaryActionButton>
			</div>

			<!-- Actual logs -->
			<div
				v-else-if="logs && logs.length > 0"
				class="logs-list"
			>
				<div
					v-for="log in logs"
					:key="log.auditLogId"
					class="log-item"
				>
					<div
						class="log-header"
						role="button"
						tabindex="0"
						:aria-expanded="isExpanded(log.auditLogId) ? 'true' : 'false'"
						:aria-controls="`log-details-${log.auditLogId}`"
						@click="toggleExpand(log.auditLogId)"
						@keydown.enter="toggleExpand(log.auditLogId)"
						@keydown.space.prevent="toggleExpand(log.auditLogId)"
					>
						<div class="log-header-left">
							<v-icon
								small
								class="expand-icon"
								:class="{ 'is-expanded': isExpanded(log.auditLogId) }"
							>
								mdi-chevron-right
							</v-icon>
							<span class="log-date">
								{{ formatTimestamp(log.timestamp) }}
							</span>
							<span
								class="log-method"
								:class="getMethodClass(log.httpMethod)"
							>
								{{ log.httpMethod }}
							</span>
							<span class="log-operation-name">
								{{ log.operation }}
							</span>
						</div>
						<span
							class="log-status"
							:class="getStatusClass(log.statusCode)"
						>
							{{ log.statusCode }}
						</span>
					</div>

					<!-- Expanded details -->
					<div
						v-if="isExpanded(log.auditLogId)"
						:id="`log-details-${log.auditLogId}`"
						class="log-details"
					>
						<div class="detail-row">
							<span class="detail-label">
								Resource Type:
							</span>
							<span class="detail-value">
								{{ log.resourceType }}
							</span>
						</div>
						<div
							v-if="log.resourceId"
							class="detail-row"
						>
							<span class="detail-label">
								Resource ID:
							</span>
							<span class="detail-value monospace">
								{{ log.resourceId }}
							</span>
						</div>
						<div class="detail-row">
							<span class="detail-label">
								Request Path:
							</span>
							<span class="detail-value monospace">
								{{ log.requestPath }}
							</span>
						</div>
						<div
							v-if="log.affectedUserEmail"
							class="detail-row"
						>
							<span class="detail-label">
								Affected User:
							</span>
							<span class="detail-value">
								{{ log.affectedUserEmail }}
							</span>
						</div>
						<div class="detail-row">
							<span class="detail-label">
								Client IP:
							</span>
							<span class="detail-value monospace">
								{{ log.clientIpAddress }}
							</span>
						</div>
						<div class="detail-row">
							<span class="detail-label">
								Duration:
							</span>
							<span class="detail-value">
								{{ log.durationMs }}ms
							</span>
						</div>
						<div class="detail-row">
							<span class="detail-label">
								Correlation ID:
							</span>
							<span class="detail-value monospace">
								{{ log.correlationId }}
							</span>
						</div>
						<div
							v-if="log.filter"
							class="detail-row"
						>
							<span class="detail-label">
								Filter:
							</span>
							<span class="detail-value monospace">
								{{ log.filter }}
							</span>
						</div>
						<div
							v-if="log.errorMessage"
							class="detail-row error-row"
						>
							<span class="detail-label">
								Error:
							</span>
							<span class="detail-value">
								{{ log.errorMessage }}
							</span>
						</div>
						<div
							v-if="log.requestBody"
							class="detail-block collapsible-block"
						>
							<div
								class="collapsible-header"
								role="button"
								tabindex="0"
								:aria-expanded="isBodyExpanded(log.auditLogId, 'request') ? 'true' : 'false'"
								:aria-controls="`request-body-${log.auditLogId}`"
								@click="toggleBodyExpand(log.auditLogId, 'request')"
								@keydown.enter="toggleBodyExpand(log.auditLogId, 'request')"
								@keydown.space.prevent="toggleBodyExpand(log.auditLogId, 'request')"
							>
								<span class="detail-label">
									Request Body:
								</span>
								<span class="body-size">
									{{ getBodySize(log.requestBody) }}
								</span>
								<v-icon
									small
									class="expand-icon"
									:class="{ 'is-expanded': isBodyExpanded(log.auditLogId, 'request') }"
								>
									mdi-chevron-right
								</v-icon>
							</div>
							<div
								v-if="isBodyExpanded(log.auditLogId, 'request')"
								:id="`request-body-${log.auditLogId}`"
								class="json-container"
							>
								<vue-json-pretty
									v-if="getParsedJson(log.auditLogId, 'request', log.requestBody)"
									:data="getParsedJson(log.auditLogId, 'request', log.requestBody)"
									:deep="2"
									show-line
								/>
								<pre
									v-else
									class="detail-json-fallback"
								>{{ log.requestBody }}</pre>
							</div>
						</div>
						<div
							v-if="log.responseBody"
							class="detail-block collapsible-block"
						>
							<div
								class="collapsible-header"
								role="button"
								tabindex="0"
								:aria-expanded="isBodyExpanded(log.auditLogId, 'response') ? 'true' : 'false'"
								:aria-controls="`response-body-${log.auditLogId}`"
								@click="toggleBodyExpand(log.auditLogId, 'response')"
								@keydown.enter="toggleBodyExpand(log.auditLogId, 'response')"
								@keydown.space.prevent="toggleBodyExpand(log.auditLogId, 'response')"
							>
								<span class="detail-label">
									Response Body:
								</span>
								<span class="body-size">
									{{ getBodySize(log.responseBody) }}
								</span>
								<v-icon
									small
									class="expand-icon"
									:class="{ 'is-expanded': isBodyExpanded(log.auditLogId, 'response') }"
								>
									mdi-chevron-right
								</v-icon>
							</div>
							<div
								v-if="isBodyExpanded(log.auditLogId, 'response')"
								:id="`response-body-${log.auditLogId}`"
								class="json-container"
							>
								<vue-json-pretty
									v-if="getParsedJson(log.auditLogId, 'response', log.responseBody)"
									:data="getParsedJson(log.auditLogId, 'response', log.responseBody)"
									:deep="2"
									show-line
								/>
								<pre
									v-else
									class="detail-json-fallback"
								>{{ log.responseBody }}</pre>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div
				v-else
				class="no-logs"
			>
				<v-icon
					size="48"
					color="grey lighten-1"
					class="mb-4"
				>
					mdi-text-box-search-outline
				</v-icon>
				<p>
					No SCIM logs found for this configuration.
				</p>
			</div>
		</template>

		<template #footer>
			<div class="footer-content">
				<SecondaryActionButton @click="$emit('close')">
					Close
				</SecondaryActionButton>
				<div
					v-if="totalCount > 0"
					class="pagination-controls"
				>
					<span class="pagination-info">
						Page {{ pageNumber }} of {{ totalPages }} ({{ totalCount }} total)
					</span>
					<v-btn
						icon
						small
						:disabled="pageNumber <= 1"
						aria-label="Previous page"
						@click="previousPage"
					>
						<v-icon small>
							mdi-chevron-left
						</v-icon>
					</v-btn>
					<v-btn
						icon
						small
						:disabled="pageNumber >= totalPages"
						aria-label="Next page"
						@click="nextPage"
					>
						<v-icon small>
							mdi-chevron-right
						</v-icon>
					</v-btn>
				</div>
			</div>
		</template>
	</SlidingSheet>
</template>

<script>
/* eslint-disable max-lines */
import VueJsonPretty from 'vue-json-pretty'
import 'vue-json-pretty/lib/styles.css'
import { SlidingSheet } from '../../../../components/component-library-wrapper.js'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import { getScimAuditLogs } from '../../../../../../shared/utils/api/sso-configuration.js'

export default {
	components: {
		SlidingSheet,
		SecondaryActionButton,
		VueJsonPretty
	},
	props: {
		isOpen: {
			type: Boolean,
			default: false
		},
		configurationName: {
			type: String,
			default: ''
		},
		configurationId: {
			type: Number,
			default: null
		}
	},
	data () {
		return {
			logs: [],
			loading: false,
			loadError: false,
			pageNumber: 1,
			pageSize: 20,
			totalCount: 0,
			totalPages: 0,
			expandedLogIds: new Set(),
			expandedBodies: new Set(),
			parsedJsonCache: {}
		}
	},
	computed: {
		skeletonCount () {
			return 3
		},
		sheetWidth () {
			// Responsive width based on viewport
			if (typeof window !== 'undefined' && window.innerWidth < 600) {
				return '100%'
			}
			if (typeof window !== 'undefined' && window.innerWidth < 960) {
				return '90%'
			}
			return '800px'
		}
	},
	watch: {
		isOpen (newVal) {
			if (newVal && this.configurationId) {
				this.resetPagination()
				this.loadLogs()
			}
		},
		configurationId (newVal) {
			if (newVal && this.isOpen) {
				this.resetPagination()
				this.loadLogs()
			}
		}
	},
	mounted () {
		if (this.isOpen && this.configurationId) {
			this.loadLogs()
		}
	},
	methods: {
		resetPagination () {
			this.pageNumber = 1
			this.totalCount = 0
			this.totalPages = 0
			this.expandedLogIds = new Set()
			this.expandedBodies = new Set()
			this.parsedJsonCache = {}
			this.loadError = false
		},
		isExpanded (auditLogId) {
			return this.expandedLogIds.has(auditLogId)
		},
		toggleExpand (auditLogId) {
			if (this.expandedLogIds.has(auditLogId)) {
				this.expandedLogIds.delete(auditLogId)
			} else {
				this.expandedLogIds.add(auditLogId)
			}
			// Force reactivity update for Set
			this.expandedLogIds = new Set(this.expandedLogIds)
		},
		toggleBodyExpand (auditLogId, type) {
			const key = `${auditLogId}-${type}`
			if (this.expandedBodies.has(key)) {
				this.expandedBodies.delete(key)
			} else {
				this.expandedBodies.add(key)
			}
			// Force reactivity update for Set
			this.expandedBodies = new Set(this.expandedBodies)
		},
		isBodyExpanded (auditLogId, type) {
			const key = `${auditLogId}-${type}`
			return this.expandedBodies.has(key)
		},
		getParsedJson (auditLogId, type, jsonString) {
			const cacheKey = `${auditLogId}-${type}`
			if (this.parsedJsonCache[cacheKey] !== undefined) {
				return this.parsedJsonCache[cacheKey]
			}
			const parsed = this.parseJson(jsonString)
			this.parsedJsonCache[cacheKey] = parsed
			return parsed
		},
		getBodySize (body) {
			if (!body) return ''
			const bytes = new Blob([body]).size
			if (bytes < 1024) return `${bytes} B`
			if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
			return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
		},
		parseJson (jsonString) {
			if (!jsonString) return null
			try {
				return JSON.parse(jsonString)
			} catch {
				return null
			}
		},
		getMethodClass (method) {
			const methodUpper = (method || '').toUpperCase()
			return {
				'method-get': methodUpper === 'GET',
				'method-post': methodUpper === 'POST',
				'method-put': methodUpper === 'PUT',
				'method-patch': methodUpper === 'PATCH',
				'method-delete': methodUpper === 'DELETE'
			}
		},
		formatTimestamp (timestamp) {
			if (!timestamp) return ''
			const date = new Date(timestamp)
			// Use browser's locale for user-appropriate formatting
			return date.toLocaleString(undefined, {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit'
			})
		},
		getStatusClass (statusCode) {
			if (statusCode >= 200 && statusCode < 300) {
				return 'status-success'
			} else if (statusCode >= 400 && statusCode < 500) {
				return 'status-warning'
			} else if (statusCode >= 500) {
				return 'status-error'
			}
			return 'status-info'
		},
		async loadLogs () {
			this.loading = true
			this.loadError = false
			// Reset expanded states when loading new page
			this.expandedLogIds = new Set()
			this.expandedBodies = new Set()
			this.parsedJsonCache = {}

			try {
				const { data } = await getScimAuditLogs(this.configurationId, {
					pageNumber: this.pageNumber,
					pageSize: this.pageSize
				})
				this.logs = data.auditLogs || []
				this.totalCount = data.totalCount || 0
				this.totalPages = data.totalPages || Math.ceil(this.totalCount / this.pageSize)
			} catch (error) {
				console.error('Failed to load SCIM logs:', error)
				this.loadError = true
				this.logs = []
				this.totalCount = 0
				this.totalPages = 0
			} finally {
				this.loading = false
			}
		},
		previousPage () {
			if (this.pageNumber > 1) {
				this.pageNumber--
				this.loadLogs()
			}
		},
		nextPage () {
			if (this.pageNumber < this.totalPages) {
				this.pageNumber++
				this.loadLogs()
			}
		}
	}
}
</script>

<style scoped>
.logs-list {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.log-item {
	border: 1px solid var(--global-border-color);
	border-radius: var(--radius-sm, 4px);
	background-color: var(--global-main-background-color);
	overflow: hidden;
}

.log-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px;
	cursor: pointer;
	user-select: none;
}

.log-header:hover {
	background-color: var(--global-hover-background, rgba(0, 0, 0, 0.04));
}

.log-header:focus {
	outline: 2px solid var(--global-primary, #406A94);
	outline-offset: -2px;
}

.log-header:focus:not(:focus-visible) {
	outline: none;
}

.log-header:focus-visible {
	outline: 2px solid var(--global-primary, #406A94);
	outline-offset: -2px;
}

.log-header-left {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.expand-icon {
	transition: transform 0.2s ease;
}

.expand-icon.is-expanded {
	transform: rotate(90deg);
}

.log-date {
	font-size: 13px;
	color: var(--global-muted-foreground);
}

.log-method {
	padding: 2px 6px;
	border-radius: var(--radius-sm, 4px);
	font-weight: bold;
	font-size: 11px;
	font-family: monospace;
}

.method-get {
	background-color: var(--method-get-bg, #e3f2fd);
	color: var(--method-get-color, #1565c0);
}

.method-post {
	background-color: var(--method-post-bg, #e8f5e9);
	color: var(--method-post-color, #2e7d32);
}

.method-put {
	background-color: var(--method-put-bg, #fff3e0);
	color: var(--method-put-color, #ef6c00);
}

.method-patch {
	background-color: var(--method-patch-bg, #f3e5f5);
	color: var(--method-patch-color, #7b1fa2);
}

.method-delete {
	background-color: var(--method-delete-bg, #ffebee);
	color: var(--method-delete-color, #c62828);
}

.log-operation-name {
	font-size: 14px;
	font-weight: 500;
	color: var(--global-foreground);
}

.log-status {
	padding: 2px 8px;
	border-radius: var(--radius-sm, 4px);
	font-weight: bold;
	font-size: 12px;
	flex-shrink: 0;
}

.status-success {
	background-color: var(--status-success-bg, #d4edda);
	color: var(--status-success-color, #155724);
}

.status-error {
	background-color: var(--status-error-bg, #f8d7da);
	color: var(--status-error-color, #721c24);
}

.status-warning {
	background-color: var(--status-warning-bg, #fff3cd);
	color: var(--status-warning-color, #856404);
}

.status-info {
	background-color: var(--status-info-bg, #d1ecf1);
	color: var(--status-info-color, #0c5460);
}

.log-details {
	padding: 0 12px 12px 36px;
	border-top: 1px solid var(--global-border-color);
	background-color: var(--global-secondary-background, rgba(0, 0, 0, 0.02));
}

.detail-row {
	display: flex;
	gap: 8px;
	padding: 6px 0;
	border-bottom: 1px solid var(--global-border-color);
	font-size: 13px;
}

.detail-row:last-child {
	border-bottom: none;
}

.detail-label {
	font-weight: 600;
	color: var(--global-muted-foreground);
	min-width: 120px;
	flex-shrink: 0;
}

.detail-value {
	color: var(--global-foreground);
	word-break: break-all;
}

.detail-value.monospace {
	font-family: monospace;
	font-size: 12px;
}

.detail-block {
	padding: 8px 0;
	border-bottom: 1px solid var(--global-border-color);
}

.detail-block:last-child {
	border-bottom: none;
}

.detail-block .detail-label {
	display: block;
	margin-bottom: 4px;
}

.collapsible-block .detail-label {
	display: inline;
	margin-bottom: 0;
}

.collapsible-header {
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	padding: 6px 0;
	user-select: none;
	font-size: 13px;
}

.collapsible-header:hover {
	opacity: 0.8;
}

.collapsible-header:focus {
	outline: 2px solid var(--global-primary, #406A94);
	outline-offset: 2px;
}

.collapsible-header:focus:not(:focus-visible) {
	outline: none;
}

.collapsible-header:focus-visible {
	outline: 2px solid var(--global-primary, #406A94);
	outline-offset: 2px;
}

.collapsible-header .expand-icon {
	margin-left: auto;
	flex-shrink: 0;
}

.collapsible-header .body-size {
	font-size: 12px;
	color: var(--global-muted-foreground);
	font-family: monospace;
}

.json-container {
	border: 1px solid var(--global-border-color);
	border-radius: var(--radius-sm, 4px);
	padding: 8px;
	background-color: var(--global-main-background-color);
	max-height: 300px;
	overflow: auto;
	margin-top: 8px;
	font-size: 11px;
}

.json-container ::v-deep .vjs-tree,
.json-container ::v-deep .vjs-tree * {
	font-size: 11px !important;
	line-height: 1.4 !important;
	font-family: 'Consolas', 'Monaco', 'Courier New', monospace !important;
}

.detail-json-fallback {
	font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
	font-size: 11px;
	line-height: 1.4;
	white-space: pre-wrap;
	word-break: break-word;
	margin: 0;
	color: var(--global-foreground);
}

.collapsible-header .body-size {
	margin-left: 4px;
}

.collapsible-header .expand-icon {
	margin-left: auto;
}

.error-row .detail-value {
	color: var(--status-error-color, #721c24);
}

.error-state {
	text-align: center;
	padding: 40px 20px;
	display: flex;
	flex-direction: column;
	align-items: center;
}

.error-message {
	color: var(--global-foreground);
	margin-bottom: 16px;
}

.no-logs {
	text-align: center;
	padding: 40px 20px;
	color: var(--global-foreground);
	display: flex;
	flex-direction: column;
	align-items: center;
}

.footer-content {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
}

.pagination-controls {
	display: flex;
	align-items: center;
	gap: 8px;
}

.pagination-info {
	font-size: 14px;
	color: var(--global-foreground);
}

/* Skeleton loader styles */
.log-skeleton {
	border: 1px solid var(--global-border-color);
	border-radius: var(--radius-sm, 4px);
	padding: 12px;
	background-color: var(--global-main-background-color);
	animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 8px;
}

.skeleton-date {
	width: 140px;
	height: 14px;
	background-color: var(--global-border-color);
	border-radius: var(--radius-sm, 4px);
}

.skeleton-status {
	width: 60px;
	height: 20px;
	background-color: var(--global-border-color);
	border-radius: var(--radius-sm, 4px);
}

.skeleton-message {
	width: 80%;
	height: 14px;
	background-color: var(--global-border-color);
	border-radius: var(--radius-sm, 4px);
}

@keyframes pulse {
	0% {
		opacity: 1;
	}
	50% {
		opacity: 0.6;
	}
	100% {
		opacity: 1;
	}
}
</style>
